/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file licenseList.ts
 * @desc Created on Mon Apr 28 2025 12:13:09
 * @copyright All rights reserved @ Inarix
 */

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  bindLicenseUser,
  bindPack,
  fetchAvailPacks,
  fetchLicenses,
  unlinkLicenseUser,
} from "../../actions/licenses/licenseListAsyncActions";
import { OrgGroupTypeIds } from "../../actions/users/userTypes";
import { RootState } from "../../../configureStore";
import {
  ExtendedLicense,
  LicenseStatus,
  PackInstance,
} from "../../actions/licenses/licenceTypes";
import {
  licenseIsInfinite,
  remainingCredsPct,
} from "../../../components/licenses/LicenceCredits";
import { baseCases } from "../../utils/queryWrapper";
import { logOut } from "../layout";
import { InarixGridFilterModel } from "../dataTable/data";
import { applyLocalFilter } from "../../../components/helpers/helpersSearchs";

const THIRTY_DAYS_MS = 60 * 60 * 24 * 30 * 1000;

export type LicenseNotification = {
  open: boolean;
  severity: "success" | "error";
  message: string;
};

export type LicenseListState = {
  loading: boolean;
  error: string;
  licenses: ExtendedLicense[];
  availPacks: PackInstance[];
  licenseIdToIdx: Record<string, number>;
  licenseGidToIdx: Record<number, number[]>;
  packIdToIdx: Record<string, number>;
  unassignedOnly: boolean;
  unassigned: number;
  lowOrEmpty: number;
  expiring: number;
  expired: number;
  upcoming: number;
  focusedLicense: string;
  licenseId: string;
  notification: LicenseNotification;
  //custom filter component
  filterModel: InarixGridFilterModel;
  showFilterForm: boolean;
  preselectedFilterField: string | null;
  //custom filter component
  openCells: boolean;
  usersWithNoActiveLicenses: any[];
  assignToUserId: string;
};

export const initialState: LicenseListState = {
  loading: false,
  error: "",
  licenses: [],
  availPacks: [],
  licenseIdToIdx: {},
  licenseGidToIdx: {},
  packIdToIdx: {},
  unassignedOnly: false,
  unassigned: 0,
  lowOrEmpty: 0,
  expiring: 0,
  expired: 0,
  upcoming: 0,

  focusedLicense: "",

  licenseId: "",
  notification: { open: false, severity: "success", message: "" },
  //custom filter component
  filterModel: { items: [] },
  showFilterForm: false,
  preselectedFilterField: null,
  //custom filter component
  openCells: false,
  usersWithNoActiveLicenses: [],
  assignToUserId: "",
};

export const selectUnassignedCount = (state: RootState) =>
  state.LicensesReducer.unassigned;
export const selectLowOrEmptyCount = (state: RootState) =>
  state.LicensesReducer.lowOrEmpty;
export const selectExpiringCount = (state: RootState) =>
  state.LicensesReducer.expiring;
export const selectExpiredCount = (state: RootState) =>
  state.LicensesReducer.expired;
export const selectTotalLicensesCount = (state: RootState) =>
  state.LicensesReducer.licenses.length;
export const selectAvailPackCount = (state: RootState) =>
  state.LicensesReducer.availPacks.length;
export const selectLicenseLoading = (state: RootState) =>
  state.LicensesReducer.loading;
export const selectUpcomingCount = (state: RootState) =>
  state.LicensesReducer.upcoming;
export const selectModalLicense = (state: RootState) =>
  state.LicensesReducer.focusedLicense
    ? state.LicensesReducer.licenses[
        state.LicensesReducer.licenseIdToIdx[
          state.LicensesReducer.focusedLicense
        ]
      ]
    : null;

export const filterselectors = (state: RootState) => ({
  filterModel: state.LicensesReducer.filterModel,
  showFilterForm: state.LicensesReducer.showFilterForm,
  preselectedFilterField: state.LicensesReducer.preselectedFilterField,
  licenses: state.LicensesReducer.licenses,
});

export const licenseAssignation = (state: RootState) => ({
  licenseId: state.LicensesReducer.licenseId,
});

export const selectAvailPacks = (state: RootState) =>
  state.LicensesReducer.availPacks;

export const selectLicenseNotification = (state: RootState) =>
  state.LicensesReducer.notification;

export const selectLicencesAndOwners = createSelector(
  (state: RootState) => state.UsersReducer.users.data,
  (state: RootState) => state.LicensesReducer.licenses,
  (state: RootState) => state.LocationsReducer.locations,
  (state: RootState) => state.LicensesReducer.filterModel,
  selectAvailPackCount,
  (users, licenses, locations, filterModel, availPacksCount) => {
    const idToGeoName = locations.reduce<Record<number, string>>((dict, l) => {
      dict[l.id] = l.name;
      return dict;
    }, {});
    const groupToUser = users.reduce<
      Record<number, { username: string; geoId: number; userId: string }>
    >((dict, u) => {
      u.groups?.forEach((g) => {
        if (g.typeId == OrgGroupTypeIds.user) {
          dict[g.id] = {
            username: u.username,
            geoId: u.associatedGeoPoint?.[0]?.id,
            userId: String(u.id),
          };
        }
      });
      return dict;
    }, {});

    const mappedLicenses = licenses.map((l) => {
      if (!l.ownerGroupId || l.ownerGroup?.typeId != OrgGroupTypeIds.user) {
        return { ...l, hasPacks: !!availPacksCount };
      }

      return {
        ...l,
        siteName: idToGeoName[groupToUser[l.ownerGroupId]?.geoId],
        username: groupToUser[l.ownerGroupId]?.username,
        userId: groupToUser[l.ownerGroupId]?.userId ?? null,
        hasPacks: !!availPacksCount,
      };
    });

    let filteredLicenses = mappedLicenses;

    if (filterModel?.items && filterModel.items.length > 0) {
      filteredLicenses = mappedLicenses.filter((license) => {
        return filterModel.items.every((filterItem) => {
          if (!filterItem.value || filterItem.value === "") {
            return true;
          }

          if (
            filterItem.service === "text" ||
            filterItem.service === "operator"
          ) {
            return applyLocalFilter(filterItem, license);
          }

          if (filterItem.service === "special-licenses") {
            if (
              filterItem.value === "unassigned" &&
              filterItem.operator === "$eq"
            ) {
              return (
                !license.ownerGroupId ||
                license.ownerGroup?.typeId != OrgGroupTypeIds.user
              );
            }

            if (
              filterItem.value === "unassigned" &&
              filterItem.operator === "$ne"
            ) {
              return !(
                !license.ownerGroupId ||
                license.ownerGroup?.typeId != OrgGroupTypeIds.user
              );
            }
            if (
              filterItem.value === "runningLow" &&
              filterItem.operator === "$eq"
            ) {
              const remaining =
                new Date(license.expiryDate).getTime() - Date.now();
              if (remaining <= 0 || licenseIsInfinite(license)) return false;
              const { pct } = remainingCredsPct(license);
              return pct <= 0.2;
            }

            if (
              filterItem.value === "runningLow" &&
              filterItem.operator === "$ne"
            ) {
              const remaining =
                new Date(license.expiryDate).getTime() - Date.now();
              if (remaining <= 0 || licenseIsInfinite(license)) return true;
              const { pct } = remainingCredsPct(license);
              return !(pct <= 0.2);
            }

            if (
              filterItem.value === "expiringSoon" &&
              filterItem.operator === "$eq"
            ) {
              const remaining =
                new Date(license.expiryDate).getTime() - Date.now();
              return remaining > 0 && remaining < THIRTY_DAYS_MS;
            }

            if (
              filterItem.value === "expired" &&
              filterItem.operator === "$eq"
            ) {
              return new Date(license.expiryDate).getTime() - Date.now() <= 0;
            }

            if (
              filterItem.value === "expiringSoon" &&
              filterItem.operator === "$ne"
            ) {
              const remaining =
                new Date(license.expiryDate).getTime() - Date.now();
              return !(remaining > 0 && remaining < THIRTY_DAYS_MS);
            }

            if (
              filterItem.value === "expired" &&
              filterItem.operator === "$ne"
            ) {
              return !(
                new Date(license.expiryDate).getTime() - Date.now() <=
                0
              );
            }
            if (
              filterItem.value === "active" &&
              filterItem.operator === "$eq"
            ) {
              const remaining =
                new Date(license.expiryDate).getTime() - Date.now();
              const activated =
                new Date(license.activationDate).getTime() <= Date.now();
              return (
                remaining > 0 &&
                activated &&
                license.ownerGroupId &&
                license.ownerGroup?.typeId === OrgGroupTypeIds.user
              );
            }
            if (
              filterItem.value === "active" &&
              filterItem.operator === "$ne"
            ) {
              const remaining =
                new Date(license.expiryDate).getTime() - Date.now();
              const activated =
                new Date(license.activationDate).getTime() <= Date.now();
              return !(
                remaining > 0 &&
                activated &&
                license.ownerGroupId &&
                license.ownerGroup?.typeId === OrgGroupTypeIds.user
              );
            }
            if (
              filterItem.value === "upcoming" &&
              filterItem.operator === "$eq"
            ) {
              return (
                !!license.ownerGroupId &&
                license.ownerGroup?.typeId === OrgGroupTypeIds.user &&
                new Date(license.activationDate).getTime() > Date.now()
              );
            }
            if (
              filterItem.value === "upcoming" &&
              filterItem.operator === "$ne"
            ) {
              return !(
                !!license.ownerGroupId &&
                license.ownerGroup?.typeId === OrgGroupTypeIds.user &&
                new Date(license.activationDate).getTime() > Date.now()
              );
            }
          }
          return true;
        });
      });
    }

    return filteredLicenses;
  },
);

export const selectUsersWithNoLicenses = createSelector(
  (state: RootState) => state.UsersReducer.users.data,
  (state: RootState) => state.LicensesReducer.licenses,
  (users, licenses) => {
    const assignedGroupIds = new Set(
      licenses
        .filter(
          (l) =>
            l.ownerGroupId && l.ownerGroup?.typeId === OrgGroupTypeIds.user,
        )
        .map((l) => l.ownerGroupId as number),
    );

    return users.filter((user) => {
      const userGroupId = user.groups?.find(
        (g: any) => g.typeId === OrgGroupTypeIds.user,
      )?.id;
      return !userGroupId || !assignedGroupIds.has(userGroupId);
    });
  },
);

export const selectUnassignedLicenses = createSelector(
  (state: RootState) => state.LicensesReducer.licenses,
  (licenses) =>
    licenses.filter(
      (l) =>
        (!l.ownerGroupId || l.ownerGroup?.typeId !== OrgGroupTypeIds.user) &&
        new Date(l.expiryDate).getTime() > Date.now(),
    ),
);

export const selectLicenseById = (licenseId: string) => (state: RootState) => {
  const idx = state.LicensesReducer.licenseIdToIdx[licenseId];
  return idx !== undefined
    ? (state.LicensesReducer.licenses[idx] ?? null)
    : null;
};

export const LicensesFilters = createSelector(
  (state: RootState) => state.LicensesReducer,
  (LicensesReducer) => ({
    filterModel: LicensesReducer.filterModel,
    showFilterForm: LicensesReducer.showFilterForm,
    preselectedFilterField: LicensesReducer.preselectedFilterField,
    openCells: LicensesReducer.openCells,
  }),
);

const CreateLicenseListSlice = createSlice({
  name: "LicensesReducer",
  initialState,
  reducers: {
    setOpenCells(state, action) {
      return {
        ...state,
        openCells: action.payload,
      };
    },

    setLicenseId(state, action) {
      return {
        ...state,
        licenseId: action.payload,
      };
    },
    setFilterModel(state, action) {
      return {
        ...state,
        filterModel: action.payload,
      };
    },

    setShowFilterForm(state, action) {
      return {
        ...state,
        showFilterForm: action.payload,
      };
    },

    setPreselectedFilterField(state, action) {
      return {
        ...state,
        preselectedFilterField: action.payload,
      };
    },

    toggleUnassigned(state) {
      state.unassignedOnly = !state.unassignedOnly;
    },
    focusedLicense(state, action: PayloadAction<string>) {
      state.focusedLicense = action.payload;
    },
    clearLicenseNotification(state) {
      state.notification = { open: false, severity: "success", message: "" };
    },

    setAssignToUserId(state, action: PayloadAction<string>) {
      state.assignToUserId = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(logOut, () => {
      return initialState;
    });
    baseCases(fetchLicenses, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idToIdx: Record<string, number> = {};
      const gIdToIdx: Record<number, number[]> = {};
      let unassigned = 0;
      let lowOrEmpty = 0;
      let expiring = 0;
      let expired = 0;
      let upcoming = 0;

      action.payload.forEach((li, idx) => {
        idToIdx[li.id] = idx;
        if (li.ownerGroupId) {
          if (gIdToIdx[li.ownerGroupId]) {
            gIdToIdx[li.ownerGroupId].push(idx);
          } else {
            gIdToIdx[li.ownerGroupId] = [idx];
          }
        }

        if (
          li.ownerGroupId &&
          li.ownerGroup?.typeId === OrgGroupTypeIds.user &&
          new Date(li.activationDate).getTime() > Date.now()
        ) {
          upcoming++;
        }

        if (li.statusConstantId === LicenseStatus.expired) {
          expired++;
        } else if (li.statusConstantId < LicenseStatus.expired) {
          if (!li.ownerGroup || li.ownerGroup?.typeId != OrgGroupTypeIds.user) {
            unassigned++;
          }

          const remaining = new Date(li.expiryDate).getTime() - Date.now();
          if (remaining > 0 && remaining < THIRTY_DAYS_MS) {
            expiring++;
          }
          if (remaining <= 0) {
            expired++;
          }

          li.availableCredits /= 1000;
          li.packs?.forEach((p) => (p.initialCredits /= 1000));

          const { pct } = remainingCredsPct(li);
          if (pct <= 0.2 && remaining > 0 && !licenseIsInfinite(li)) {
            lowOrEmpty++;
          }
        }
      });

      state.unassigned = unassigned;
      state.lowOrEmpty = lowOrEmpty;
      state.expiring = expiring;
      state.expired = expired;
      state.upcoming = upcoming;
      state.licenses = action.payload;
      state.licenseIdToIdx = idToIdx;
      state.licenseGidToIdx = gIdToIdx;
    });

    baseCases(fetchAvailPacks, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idToIdx: Record<string, number> = {};
      action.payload.forEach((p, idx) => {
        p.initialCredits /= 1000;
        idToIdx[p.id] = idx;
      });

      state.availPacks = action.payload;
      state.packIdToIdx = idToIdx;
    });

    builder.addCase(unlinkLicenseUser.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(unlinkLicenseUser.fulfilled, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idx = state.licenseIdToIdx[action.payload.id];
      if (state.licenses[idx]) {
        state.licenses[idx].ownerGroupId = null;
        state.licenses[idx].ownerGroup = undefined;
        state.licenses[idx].statusConstantId = action.payload.statusConstantId;
      }
      state.notification = {
        open: true,
        severity: "success",
        message: "unlinkUser_success",
      };
    });

    builder.addCase(unlinkLicenseUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.notification = {
        open: true,
        severity: "error",
        message: "unlinkUser_error",
      };
    });

    baseCases(bindLicenseUser, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idx = state.licenseIdToIdx[action.payload.id];
      if (state.licenses[idx]) {
        state.licenses[idx].ownerGroupId = action.payload.ownerGroupId;
        state.licenses[idx].ownerGroup = action.payload.ownerGroup;
      }
    });

    baseCases(bindPack, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const pIdx = state.packIdToIdx[action.payload.id];
      if (state.availPacks[pIdx]) {
        state.availPacks.splice(pIdx, 1);
        delete state.packIdToIdx[action.payload.id];
        // Rebuild packIdToIdx since indices shifted after splice
        state.availPacks.forEach((p, idx) => {
          state.packIdToIdx[p.id] = idx;
        });
      }

      const lId = action.payload.boundToLicenseInstanceId;
      if (!lId) {
        return;
      }
      const lIdx = state.licenseIdToIdx[lId];
      if (state.licenses[lIdx]) {
        // Divide by 1000 to match the normalization done in fetchAvailPacks
        const normalizedPack = {
          ...action.payload,
          initialCredits: action.payload.initialCredits / 1000,
        };
        state.licenses[lIdx].packs.push(normalizedPack);
      }
    });
  },
});

export const {
  setLicenseId,
  toggleUnassigned,
  focusedLicense,
  setFilterModel,
  setOpenCells,
  setShowFilterForm,
  setPreselectedFilterField,
  clearLicenseNotification,
  setAssignToUserId,
} = CreateLicenseListSlice.actions;
export const LicensesReducer = CreateLicenseListSlice.reducer;
